import { type DragEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";

import {
  AssetActionsDialog,
  type AssetActionDone,
  type AssetDialogState,
} from "@/components/filemanager/AssetActionsDialog";
import { ComparePickerDialog } from "@/components/compare/ComparePickerDialog";
import { AssetCard } from "@/components/filemanager/AssetCard";
import { AssetHistory } from "@/components/filemanager/AssetHistory";
import { Breadcrumb } from "@/components/filemanager/Breadcrumb";
import { DropUploadOverlay } from "@/components/filemanager/DropUploadOverlay";
import { FileManagerGrid } from "@/components/filemanager/FileManagerGrid";
import { FileManagerList, type FileManagerItem } from "@/components/filemanager/FileManagerList";
import { RenderLogsModal } from "@/components/filemanager/RenderLogsModal";
import {
  FolderActionsDialog,
  type FolderActionDone,
  type FolderDialogState,
} from "@/components/filemanager/FolderActionsDialog";
import { Filters } from "@/components/filemanager/Filters";
import { FolderCard } from "@/components/filemanager/FolderCard";
import { UploadConfirmDialog } from "@/components/filemanager/UploadConfirmDialog";
import { UploadPdfButton } from "@/components/filemanager/UploadPdfButton";
import { AssetPagination } from "@/components/filemanager/AssetPagination";
import { SortOrderSelect } from "@/components/filemanager/SortOrderSelect";
import {
  UploadQueuePanel,
  type UploadQueueItem,
} from "@/components/filemanager/UploadQueuePanel";
import { NotesSidePanel } from "@/components/filemanager/NotesSidePanel";
import { ViewModeToggle } from "@/components/filemanager/ViewModeToggle";
import type { AssetListItem, AssetStatus, FolderItem } from "@/lib/api";
import { api, ApiError } from "@/lib/api";
import {
  collectFromDataTransfer,
  collectFromFileList,
  folderRoute,
  needsConfirmation,
  planUpload,
  type CollectedUpload,
  type SkippedItem,
  type UploadPlan,
} from "@/lib/folderUpload";
import { sortFolders } from "@/lib/folderOrder";
import { useCoalescedTrigger, usePolling } from "@/lib/usePolling";
import { useLastRelevantJobEvent } from "@/lib/ws";
import { useAuthStore } from "@/state/authStore";
import { useFileManagerStore } from "@/state/fileManagerStore";

const MAX_PARALLEL_UPLOADS = 3;
const MANAGER_POLL_INTERVAL_MS = 30_000;
/** Piso de tempo entre dois refreshes disparados por WebSocket. */
const WS_REFRESH_INTERVAL_MS = 2_500;
/** Eventos que mudam o que a listagem mostra. */
const EVENTOS_QUE_MUDAM_A_LISTA = new Set(["ready", "failed", "tiles_ready"]);
/**
 * O que dá para pintar no card sem ir ao servidor. `tiles_ready` fica de fora
 * de propósito: ele não muda o estado do arquivo, só adianta o que o refresh
 * coalescido vai trazer.
 */
const STATUS_DO_EVENTO: Record<string, AssetStatus | undefined> = {
  ready: "ready",
  failed: "error",
};

/** Aborto não é erro: quem cancelou foi a própria tela. */
function foiAbortado(err: unknown): boolean {
  return err instanceof DOMException && (err.name === "AbortError" || err.name === "TimeoutError");
}
interface UploadTask {
  id: string;
  file: File;
  /** Caminho lógico completo (pasta de destino + relativo): o `path` do upload. */
  path: string;
  filename: string;
}

const UPLOAD_ACCEPTED_HINT = "PDF, AI, JPEG, PNG ou TIFF";

function folderParent(fullPath: string): string {
  const index = fullPath.lastIndexOf("/");
  return index === -1 ? "" : fullPath.slice(0, index);
}

function createUploadId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function isAssetStatus(value: string): value is AssetStatus {
  return (
    value === "ready" ||
    value === "queued" ||
    value === "rendering" ||
    value === "preparing" ||
    value === "error" ||
    value === "expired"
  );
}

export function FileManager() {
  const params = useParams();
  const navigate = useNavigate();
  const folder = (params["*"] ?? "").trim().replace(/^\/+|\/+$/g, "");
  const lastRelevantEvent = useLastRelevantJobEvent();
  // Histórico, logs do render e o painel de detalhes do arquivo são de
  // administrador (F1.21). Omitir o handler já tira o item do menu de contexto,
  // no grid e na lista.
  const isAdmin = useAuthStore((state) => state.user?.is_admin === true);

  const [folderDialog, setFolderDialog] = useState<FolderDialogState>(null);
  const [assetDialog, setAssetDialog] = useState<AssetDialogState>(null);
  const [historyAsset, setHistoryAsset] = useState<AssetListItem | null>(null);
  const [logsAsset, setLogsAsset] = useState<AssetListItem | null>(null);
  const [compareSourceAsset, setCompareSourceAsset] = useState<AssetListItem | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadItems, setUploadItems] = useState<UploadQueueItem[]>([]);
  const [uploadSkipped, setUploadSkipped] = useState<SkippedItem[]>([]);
  const [uploadSystemCount, setUploadSystemCount] = useState(0);
  const [pendingUpload, setPendingUpload] = useState<{ plan: UploadPlan; targetFolder: string } | null>(null);
  const [collectingDrop, setCollectingDrop] = useState(false);
  /** A fila de verdade: uma só para todos os envios, com no máximo `MAX_PARALLEL_UPLOADS` subindo. */
  const uploadQueueRef = useRef<UploadTask[]>([]);
  const uploadWorkersRef = useRef(0);
  const [renderingAssetIds, setRenderingAssetIds] = useState<Set<string>>(new Set());
  const scrollElementRef = useRef<HTMLDivElement>(null);

  const {
    subfolders,
    assets,
    loading,
    error,
    filters,
    viewMode,
    sortOrder,
    selectedAssetId,
    page,
    pageSize,
  } =
    useFileManagerStore(
      useShallow((state) => ({
        subfolders: state.subfolders,
        assets: state.assets,
        loading: state.loading,
        error: state.error,
        filters: state.filters,
        viewMode: state.viewMode,
        sortOrder: state.sortOrder,
        page: state.page,
        pageSize: state.pageSize,
        selectedAssetId: state.selectedAssetId,
      })),
    );
  const {
    setCurrentFolder,
    setSubfolders,
    setAssets,
    updateAsset,
    setLoading,
    setError,
    setSelectedAssetId,
    setTotalAssets,
  } =
    useFileManagerStore(
      useShallow((state) => ({
        setCurrentFolder: state.setCurrentFolder,
        setSubfolders: state.setSubfolders,
        setAssets: state.setAssets,
        updateAsset: state.updateAsset,
        setLoading: state.setLoading,
        setError: state.setError,
        setSelectedAssetId: state.setSelectedAssetId,
        setTotalAssets: state.setTotalAssets,
      })),
    );

  useEffect(() => {
    setCurrentFolder(folder);
  }, [folder, setCurrentFolder]);

  const refresh = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const fileFilterActive = Boolean(
        filters.search || filters.status || filters.viewerKind || filters.hasNotes !== null,
      );
      const [foldersRes, assetsRes] = await Promise.all([
        api.listFolders(folder, {
          search: filters.search || undefined,
          recursive: Boolean(filters.search),
          signal,
        }),
        api.listAssetsPage({
          folder,
          search: filters.search || undefined,
          status: filters.status ?? undefined,
          viewer_kind: filters.viewerKind ?? undefined,
          has_notes: filters.hasNotes ?? undefined,
          recursive: fileFilterActive,
          sort: sortOrder,
          limit: pageSize,
          offset: page * pageSize,
          signal,
        }),
      ]);
      setSubfolders(foldersRes.subfolders);
      setAssets(assetsRes.items);
      setTotalAssets(assetsRes.total);
    } catch (err) {
      if (foiAbortado(err)) return;
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [
    filters.search,
    filters.status,
    filters.viewerKind,
    filters.hasNotes,
    folder,
    page,
    pageSize,
    sortOrder,
    setAssets,
    setTotalAssets,
    setError,
    setLoading,
    setSubfolders,
  ]);

  const refreshSilent = useCallback(
    async (targetFolder = folder, signal?: AbortSignal) => {
      const requestedSearch = filters.search;
      const requestedStatus = filters.status;
      const requestedViewerKind = filters.viewerKind;
      const requestedHasNotes = filters.hasNotes;
      // Refresh silencioso re-busca apenas a página visível (Fase 5 F6).
      const snapshot = useFileManagerStore.getState();
      const requestedPage = snapshot.page;
      const requestedPageSize = snapshot.pageSize;
      const requestedSort = snapshot.sortOrder;
      const fileFilterActive = Boolean(
        requestedSearch || requestedStatus || requestedViewerKind || requestedHasNotes !== null,
      );
      try {
        const [foldersRes, assetsRes] = await Promise.all([
          api.listFolders(targetFolder, {
            search: requestedSearch || undefined,
            recursive: Boolean(requestedSearch),
            signal,
          }),
          api.listAssetsPage({
            folder: targetFolder,
            search: requestedSearch || undefined,
            status: requestedStatus ?? undefined,
            viewer_kind: requestedViewerKind ?? undefined,
            has_notes: requestedHasNotes ?? undefined,
            recursive: fileFilterActive,
            sort: requestedSort,
            limit: requestedPageSize,
            offset: requestedPage * requestedPageSize,
            signal,
          }),
        ]);
        const current = useFileManagerStore.getState();
        if (
          current.currentFolder !== targetFolder ||
          current.filters.search !== requestedSearch ||
          current.filters.status !== requestedStatus ||
          current.filters.viewerKind !== requestedViewerKind ||
          current.filters.hasNotes !== requestedHasNotes ||
          current.page !== requestedPage ||
          current.sortOrder !== requestedSort
        ) {
          return;
        }
        setSubfolders(foldersRes.subfolders);
        setAssets(assetsRes.items);
        setTotalAssets(assetsRes.total);
      } catch {
        // background refresh — silently ignore errors
      }
    },
    [
      folder,
      filters.search,
      filters.status,
      filters.viewerKind,
      filters.hasNotes,
      setSubfolders,
      setAssets,
      setTotalAssets,
    ],
  );

  useEffect(() => {
    const controller = new AbortController();
    void refresh(controller.signal);
    // Trocar de pasta, de filtro ou de rota cancela a busca anterior: sem isto
    // a resposta antiga chegava depois e sobrescrevia a lista da pasta nova.
    return () => controller.abort();
  }, [refresh]);

  // ── WebSocket: a rajada inteira vira um refresh ──────────────────────────
  //
  // O backend faz broadcast de todo evento de job para todos os clientes, e
  // antes cada evento relevante refazia a listagem inteira (dois requests, 100
  // assets). Uma fila com 200 renders terminando gerava ~400 requests por aba
  // aberta — e o manager é a rota-índice, então "aba aberta" é o caso comum.
  const refreshCoalescido = useCoalescedTrigger(() => {
    void refreshSilent();
  }, WS_REFRESH_INTERVAL_MS);

  useEffect(() => {
    if (!lastRelevantEvent) return;
    if (!EVENTOS_QUE_MUDAM_A_LISTA.has(lastRelevantEvent.status)) return;
    // O estado do card vira na hora quando o evento é de um arquivo que está
    // na página; o resto (miniatura, chapas, duração) chega no refresh
    // coalescido, que é o único que gasta request.
    const novoStatus = STATUS_DO_EVENTO[lastRelevantEvent.status];
    const fileId = lastRelevantEvent.file_id;
    if (novoStatus && fileId) {
      const naPagina = useFileManagerStore
        .getState()
        .assets.find((asset) => asset.current_file_id === fileId);
      if (naPagina) updateAsset(naPagina.id, { status: novoStatus });
    }
    // Aba escondida não gasta request: quem volta para ela recebe uma volta do
    // poll na hora (`usePolling` dispara no `visibilitychange`), e é essa volta
    // que traz tudo o que aconteceu enquanto ninguém olhava. Sem esta guarda,
    // cada aba esquecida aberta no manager refazia a listagem a cada 2,5 s
    // durante uma fila inteira.
    if (typeof document !== "undefined" && document.hidden) return;
    refreshCoalescido();
  }, [lastRelevantEvent, refreshCoalescido, updateAsset]);

  // ── Poll de fundo de 30 s ────────────────────────────────────────────────
  const pollTick = useCallback(
    async (signal: AbortSignal) => {
      await refreshSilent(undefined, signal);
    },
    [refreshSilent],
  );
  usePolling(pollTick, { intervalMs: MANAGER_POLL_INTERVAL_MS });

  const handleFolderDone = useCallback(
    (result: FolderActionDone) => {
      setFolderDialog(null);
      let deferRefresh = false;
      if (result.kind === "rename") {
        const { oldPath, newPath } = result;
        if (folder === oldPath || folder.startsWith(`${oldPath}/`)) {
          if (folder === oldPath) navigate(folderRoute(newPath));
          else navigate(folderRoute(`${newPath}${folder.slice(oldPath.length)}`));
          deferRefresh = true;
        }
      } else if (result.kind === "delete") {
        const deletedPath = result.path;
        if (folder === deletedPath || folder.startsWith(`${deletedPath}/`)) {
          const parent = folderParent(deletedPath);
          navigate(folderRoute(parent));
          deferRefresh = true;
        }
      }
      if (!deferRefresh) void refresh();
    },
    [folder, navigate, refresh],
  );

  const handleAssetDone = useCallback(
    (result: AssetActionDone) => {
      setAssetDialog(null);
      if (result.kind === "delete" && result.assetId === selectedAssetId) setSelectedAssetId(null);
      void refresh();
    },
    [refresh, selectedAssetId, setSelectedAssetId],
  );

  const patchUploadItem = useCallback((id: string, patch: Partial<UploadQueueItem>) => {
    setUploadItems((current) =>
      current.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    );
  }, []);

  const uploadOne = useCallback(
    async (task: UploadTask) => {
      patchUploadItem(task.id, { status: "uploading", progress: 0, error: null });
      try {
        await api.uploadFile(task.file, {
          path: task.path,
          filename: task.filename,
          onProgress: (progress) => {
            const percent = progress.percent ?? 0;
            patchUploadItem(task.id, {
              progress: percent,
              status: percent >= 100 ? "processing" : "uploading",
            });
          },
        });
        patchUploadItem(task.id, { status: "done", progress: 100 });
        // Coalescido: antes cada arquivo concluído refazia a listagem (dois
        // requests), e uma pasta de 800 arquivos eram 1.600 listagens.
        refreshCoalescido();
      } catch (err) {
        patchUploadItem(task.id, {
          status: "error",
          error: err instanceof Error ? err.message : String(err),
        });
      }
    },
    [patchUploadItem, refreshCoalescido],
  );

  const enqueueUploads = useCallback(
    (tasks: UploadTask[]) => {
      uploadQueueRef.current.push(...tasks);
      while (
        uploadWorkersRef.current < MAX_PARALLEL_UPLOADS &&
        uploadWorkersRef.current < uploadQueueRef.current.length
      ) {
        uploadWorkersRef.current += 1;
        void (async () => {
          try {
            for (let task = uploadQueueRef.current.shift(); task; task = uploadQueueRef.current.shift()) {
              await uploadOne(task);
            }
          } finally {
            uploadWorkersRef.current -= 1;
          }
        })();
      }
    },
    [uploadOne],
  );

  const cancelQueuedUploads = useCallback(() => {
    const canceled = new Set(uploadQueueRef.current.map((task) => task.id));
    uploadQueueRef.current = [];
    setUploadItems((current) =>
      current.map((item) =>
        canceled.has(item.id) && item.status === "queued" ? { ...item, status: "canceled" as const } : item,
      ),
    );
  }, []);

  /** Pasta vazia que já existe é mescla, não erro: o 409 do backend é ignorado. */
  const createEmptyFolders = useCallback(async (paths: string[], targetFolder: string) => {
    const failed: SkippedItem[] = [];
    for (const path of paths) {
      try {
        await api.createFolder(path);
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) continue;
        const relativePath = targetFolder ? path.slice(targetFolder.length + 1) : path;
        failed.push({
          relativePath: `${relativePath}/`,
          reason: "folder_error",
          detail: err instanceof Error ? err.message : String(err),
        });
      }
    }
    if (failed.length > 0) setUploadSkipped((current) => [...current, ...failed]);
    if (paths.length > failed.length) refreshCoalescido();
  }, [refreshCoalescido]);

  const executeUploadPlan = useCallback(
    (plan: UploadPlan, targetFolder: string) => {
      setError(null);
      if (plan.skipped.length > 0) setUploadSkipped((current) => [...current, ...plan.skipped]);
      if (plan.systemCount > 0) setUploadSystemCount((current) => current + plan.systemCount);
      const tasks: UploadTask[] = plan.uploads.map((upload) => ({
        id: createUploadId(),
        file: upload.file,
        path: upload.path,
        filename: upload.filename,
      }));
      setUploadItems((current) => [
        ...current,
        ...tasks.map((task, index) => ({
          id: task.id,
          filename: task.filename,
          relativePath: plan.uploads[index].relativePath,
          size: task.file.size,
          progress: 0,
          status: "queued" as const,
          error: null,
        })),
      ]);
      enqueueUploads(tasks);
      if (plan.emptyFolders.length > 0) void createEmptyFolders(plan.emptyFolders, targetFolder);
    },
    [createEmptyFolders, enqueueUploads, setError],
  );

  /**
   * O que foi escolhido ou solto vira um plano (`planUpload`): o que não é
   * aceito é pulado e listado no painel, em vez de recusar o lote inteiro, e
   * lote grande pede confirmação antes de começar.
   */
  const startUpload = useCallback(
    (collected: CollectedUpload, targetFolder: string) => {
      const plan = planUpload(collected, targetFolder);
      if (plan.uploads.length === 0 && plan.emptyFolders.length === 0) {
        if (plan.skipped.length > 0) setUploadSkipped((current) => [...current, ...plan.skipped]);
        setError(
          plan.skipped.length > 0
            ? `Nenhum arquivo aceito: envie ${UPLOAD_ACCEPTED_HINT}. Os ignorados estão no painel de uploads.`
            : `Arraste um ou mais arquivos ${UPLOAD_ACCEPTED_HINT}.`,
        );
        return;
      }
      if (needsConfirmation(plan)) {
        setPendingUpload({ plan, targetFolder });
        return;
      }
      executeUploadPlan(plan, targetFolder);
    },
    [executeUploadPlan, setError],
  );

  const uploadFiles = useCallback(
    (files: FileList) => startUpload(collectFromFileList(Array.from(files)), folder),
    [folder, startUpload],
  );

  const confirmPendingUpload = useCallback(() => {
    if (!pendingUpload) return;
    setPendingUpload(null);
    executeUploadPlan(pendingUpload.plan, pendingUpload.targetFolder);
  }, [executeUploadPlan, pendingUpload]);

  const clearFinishedUploads = useCallback(() => {
    setUploadItems((current) =>
      current.filter(
        (item) => item.status !== "done" && item.status !== "error" && item.status !== "canceled",
      ),
    );
    setUploadSkipped([]);
    setUploadSystemCount(0);
  }, []);

  const uploadsInFlight = uploadItems.some(
    (item) => item.status === "queued" || item.status === "uploading" || item.status === "processing",
  );

  // A fila vive nesta página: fechar ou recarregar a aba corta o que falta.
  useEffect(() => {
    if (!uploadsInFlight) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [uploadsInFlight]);

  const handleDragEnter = useCallback((event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragOver = useCallback((event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "copy";
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const nextTarget = event.relatedTarget;
    if (!(nextTarget instanceof Node) || !event.currentTarget.contains(nextTarget)) {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setDragActive(false);
      // As entradas saem do `DataTransfer` na parte síncrona da coleta; depois
      // deste handler o navegador o esvazia.
      const targetFolder = folder;
      setCollectingDrop(true);
      collectFromDataTransfer(event.dataTransfer)
        .then((collected) => startUpload(collected, targetFolder))
        .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)))
        .finally(() => setCollectingDrop(false));
    },
    [folder, setError, startUpload],
  );

  const openCreateFolderDialog = useCallback(() => {
    setFolderDialog({ mode: "create" });
  }, []);

  const openRenameFolderDialog = useCallback((item: FolderItem) => {
    setFolderDialog({ mode: "rename", folder: item });
  }, []);

  const openDeleteFolderDialog = useCallback((item: FolderItem) => {
    setFolderDialog({ mode: "delete", folder: item });
  }, []);

  /**
   * A estrela vira na hora e a pasta já sobe (ou desce) na lista; se o
   * servidor recusar, volta como estava. O favorito é de todos.
   */
  const toggleFolderFavorite = useCallback(
    async (item: FolderItem) => {
      const favorite = item.favorite !== true;
      const mark = (value: boolean) =>
        setSubfolders(
          useFileManagerStore
            .getState()
            .subfolders.map((subfolder) =>
              subfolder.path === item.path ? { ...subfolder, favorite: value } : subfolder,
            ),
        );
      mark(favorite);
      try {
        await api.setFolderFavorite(item.path, favorite);
      } catch (err) {
        mark(!favorite);
        setError(err instanceof Error ? err.message : "Não foi possível mudar o favorito da pasta.");
      }
    },
    [setError, setSubfolders],
  );

  const openAssetHistory = useCallback((item: AssetListItem) => {
    setHistoryAsset(item);
  }, []);

  const openAssetRenderLogs = useCallback((item: AssetListItem) => {
    setLogsAsset(item);
  }, []);

  const openComparePicker = useCallback((item: AssetListItem) => {
    if (!item.public_slug) {
      setError("Este arquivo ainda não possui slug público para comparação.");
      return;
    }
    setCompareSourceAsset(item);
  }, [setError]);

  /**
   * «Comparar com a versão anterior»: sem seletor, porque não há o que
   * escolher — as duas versões são do MESMO asset e moram na mesma linha da
   * listagem. O que existia era o histórico no banco; o que faltava era o
   * caminho até ele.
   */
  const compareWithPreviousVersion = useCallback(
    async (item: AssetListItem) => {
      try {
        const versions = await api.listAssetVersions(item.id);
        const comparaveis = versions.filter(
          (version) => version.public_slug && version.render_ready,
        );
        if (comparaveis.length < 2) {
          setError(
            "Este arquivo não tem duas versões preparadas para comparar. Envie uma nova versão e espere o preparo terminar.",
          );
          return;
        }
        const [atual, anterior] = comparaveis;
        if (atual!.viewer_kind !== "document" || anterior!.viewer_kind !== "document") {
          setError(
            "A comparação por versões é do viewer de documento. Para arte de embalagem, use «Comparar».",
          );
          return;
        }
        // A versão ATUAL é o lado aberto — é ela que se está lendo, e é nela
        // que o que entrou tem onde ser pintado. A anterior é o «antes».
        const url = `/v/${encodeURIComponent(atual!.public_slug!)}?diff=${encodeURIComponent(anterior!.public_slug!)}`;
        window.open(url, "_blank", "noopener,noreferrer");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Não foi possível ler as versões deste arquivo.");
      }
    },
    [setError],
  );

  const openRenameAssetDialog = useCallback((item: AssetListItem) => {
    setAssetDialog({ mode: "rename", asset: item });
  }, []);

  const openDeleteAssetDialog = useCallback((item: AssetListItem) => {
    setAssetDialog({ mode: "delete", asset: item });
  }, []);

  const handleDownloadAsset = useCallback(
    async (asset: AssetListItem) => {
      const fileId = asset.current_file_id;
      if (!fileId) {
        setError("Este arquivo ainda não possui um original disponível para download.");
        return;
      }
      try {
        // O nome vai junto: é a reserva de quando o `Content-Disposition` da
        // resposta não chega ao JavaScript (chamada de outra origem).
        await api.downloadFile(fileId, asset.filename);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Erro ao iniciar download.");
      }
    },
    [setError],
  );

  const handleRenderAsset = useCallback(
    async (asset: AssetListItem, mode: "channels" | "composite", options: { layers?: boolean } = {}) => {
      const fileId = asset.current_file_id;
      if (!fileId) {
        setError("Não foi possível identificar o arquivo atual para renderização.");
        return;
      }
      if (renderingAssetIds.has(asset.id)) return;

      setRenderingAssetIds((previous) => {
        const next = new Set(previous);
        next.add(asset.id);
        return next;
      });
      setError(null);
      updateAsset(asset.id, { status: "queued" });

      try {
        const result = await api.renderFile(fileId, mode, options);
        updateAsset(asset.id, {
          status: isAssetStatus(result.status) ? result.status : "queued",
        });
        void refreshSilent();
      } catch (err) {
        updateAsset(asset.id, { status: asset.status });
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setRenderingAssetIds((previous) => {
          const next = new Set(previous);
          next.delete(asset.id);
          return next;
        });
      }
    },
    [refreshSilent, renderingAssetIds, setError, updateAsset],
  );

  const handleComparePick = useCallback(
    (target: AssetListItem) => {
      if (!compareSourceAsset?.public_slug || !target.public_slug) {
        setError("Os dois arquivos precisam ter slug público para comparação.");
        return;
      }
      // Dois documentos vão para o comparador SEMÂNTICO, que é um modo do
      // viewer de documento: o comparador visual é pixel a pixel, reusa o
      // canvas de embalagem e recusaria um render de documento com HTTP 400.
      // O arquivo de origem é o que fica na tela — ele é o lado «depois», e é
      // nele que o que entrou e o que mudou têm onde ser pintados.
      const semantico =
        compareSourceAsset.viewer_kind === "document" &&
        target.viewer_kind === "document";
      // Documento × arte (fase DA): a conferência de dizeres, com o documento
      // SEMPRE à esquerda — o servidor recusa a ordem inversa, e o revisor
      // seleciona na especificação para procurar na arte.
      const misto =
        (compareSourceAsset.viewer_kind === "document") !== (target.viewer_kind === "document");
      const [esquerda, direita] =
        misto && target.viewer_kind === "document"
          ? [target.public_slug, compareSourceAsset.public_slug]
          : [compareSourceAsset.public_slug, target.public_slug];
      const url = semantico
        ? `/v/${encodeURIComponent(compareSourceAsset.public_slug)}?diff=${encodeURIComponent(target.public_slug)}`
        : `/compare/${encodeURIComponent(esquerda)}/${encodeURIComponent(direita)}`;
      setCompareSourceAsset(null);
      // Nova aba: a comparação é uma sessão longa e o usuário costuma voltar ao
      // File Manager para escolher o próximo par.
      window.open(url, "_blank", "noopener,noreferrer");
    },
    [compareSourceAsset, setError],
  );

  const selectedAsset = useMemo(
    () => assets.find((asset) => asset.id === selectedAssetId) ?? null,
    [assets, selectedAssetId],
  );
  const items: FileManagerItem[] = useMemo(
    () => {
      const fileFilterActive = Boolean(
        filters.search || filters.status || filters.viewerKind || filters.hasNotes !== null,
      );
      // As pastas não são paginadas: aparecem só na primeira página, antes dos
      // arquivos. Os assets já vêm ordenados do servidor (Fase 5 F6), então não
      // há reordenação no cliente — só as pastas são ordenadas aqui, com as
      // favoritas na frente em qualquer ordenação.
      const folderItems =
        page > 0 || (fileFilterActive && !filters.search)
          ? []
          : sortFolders(subfolders, sortOrder).map((subfolder) => ({
              kind: "folder" as const,
              id: `folder:${subfolder.path}`,
              folder: subfolder,
            }));
      return [
        ...folderItems,
        ...assets.map((asset) => ({ kind: "asset" as const, id: `asset:${asset.id}`, asset })),
      ];
    },
    [assets, filters.search, filters.status, filters.viewerKind, filters.hasNotes, page, sortOrder, subfolders],
  );
  const visibleItems = items;

  useEffect(() => {
    scrollElementRef.current?.scrollTo({ top: 0 });
  }, [folder, filters.search, filters.status, filters.viewerKind, filters.hasNotes, page, sortOrder, viewMode]);

  // A janela virtual (A1) volta ao topo pela mesma régua do efeito acima; a
  // chave resume o que muda a listagem para a grade e a lista não precisarem
  // conhecer a página.
  const resetKey = useMemo(
    () =>
      [folder, filters.search, filters.status, filters.viewerKind, filters.hasNotes, page, sortOrder, viewMode].join("|"),
    [folder, filters.search, filters.status, filters.viewerKind, filters.hasNotes, page, sortOrder, viewMode],
  );

  const renderGridItem = useCallback(
    (item: FileManagerItem) =>
      item.kind === "folder" ? (
        <FolderCard
          key={item.id}
          folder={item.folder}
          onRename={openRenameFolderDialog}
          onDelete={openDeleteFolderDialog}
          onToggleFavorite={toggleFolderFavorite}
        />
      ) : (
        <AssetCard
          key={item.id}
          asset={item.asset}
          onRename={openRenameAssetDialog}
          onDelete={openDeleteAssetDialog}
          onRender={handleRenderAsset}
          onCompare={openComparePicker}
          onCompareVersion={
            item.asset.viewer_kind === "document" ? compareWithPreviousVersion : undefined
          }
          onHistory={isAdmin ? openAssetHistory : undefined}
          onRenderLogs={isAdmin ? openAssetRenderLogs : undefined}
          onDownload={handleDownloadAsset}
          rendering={renderingAssetIds.has(item.asset.id)}
        />
      ),
    [
      compareWithPreviousVersion,
      handleDownloadAsset,
      handleRenderAsset,
      isAdmin,
      openAssetHistory,
      openAssetRenderLogs,
      openComparePicker,
      openDeleteAssetDialog,
      openDeleteFolderDialog,
      openRenameAssetDialog,
      openRenameFolderDialog,
      renderingAssetIds,
      toggleFolderFavorite,
    ],
  );



  return (
    <div className="flex h-full min-h-0">
      <section className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="border-b border-ink-700">
          <div className="flex flex-wrap items-center justify-between gap-6 px-6 py-3">
            <div className="flex min-w-0 flex-wrap items-center gap-4">
              <button
                type="button"
                className="shrink-0 rounded border border-ink-600 bg-ink-800 px-3 py-1.5 text-xs font-medium text-ink-100 hover:border-emerald-600 hover:bg-ink-700"
                onClick={openCreateFolderDialog}
              >
                Nova pasta
              </button>
              <UploadPdfButton onFilesSelected={uploadFiles} />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <ViewModeToggle />
              <SortOrderSelect />
              <Filters />
            </div>
          </div>
          <div className="min-w-0 border-t border-ink-700 px-6 py-3">
            <Breadcrumb current={folder} />
          </div>
        </div>

        <div
          ref={scrollElementRef}
          className="relative flex-1 overflow-auto px-6 py-4 scrollbar-thin"
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {dragActive && <DropUploadOverlay />}

          {error && (
            <div className="mb-4 rounded border border-red-900 bg-red-950/30 px-3 py-2 text-sm text-red-300">
              {error}
            </div>
          )}
          {collectingDrop && (
            <div className="mb-4 rounded border border-ink-700 bg-ink-900 px-3 py-2 text-sm text-ink-300">
              Lendo as pastas soltas…
            </div>
          )}

          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs text-ink-500">{`${items.length} itens`}</span>
          </div>

          {loading && <div className="text-sm text-ink-400">Carregando...</div>}
          {!loading && items.length === 0 && (
            <div className="text-sm text-ink-500">Nada por aqui ainda.</div>
          )}

          {!loading && items.length > 0 && viewMode === "grid" && (
            <FileManagerGrid
              items={visibleItems}
              scrollRef={scrollElementRef}
              resetKey={resetKey}
              renderItem={renderGridItem}
            />
          )}

          {!loading && items.length > 0 && viewMode === "list" && (
            <FileManagerList
              items={visibleItems}
              scrollRef={scrollElementRef}
              resetKey={resetKey}
              onRenameFolder={openRenameFolderDialog}
              onDeleteFolder={openDeleteFolderDialog}
              onToggleFolderFavorite={toggleFolderFavorite}
              onRenameAsset={openRenameAssetDialog}
              onDeleteAsset={openDeleteAssetDialog}
              onRenderAsset={handleRenderAsset}
              onCompareAsset={openComparePicker}
              onCompareAssetVersion={compareWithPreviousVersion}
              onHistoryAsset={isAdmin ? openAssetHistory : undefined}
              onRenderLogsAsset={isAdmin ? openAssetRenderLogs : undefined}
              onDownloadAsset={handleDownloadAsset}
              renderingAssetIds={renderingAssetIds}
            />
          )}
        </div>
        {!loading && <AssetPagination loadedCount={assets.length} />}
      </section>
      {isAdmin && selectedAsset && <NotesSidePanel asset={selectedAsset} />}

      <FolderActionsDialog
        state={folderDialog}
        parentPath={folder}
        onClose={() => setFolderDialog(null)}
        onDone={handleFolderDone}
      />
      <AssetActionsDialog
        state={assetDialog}
        onClose={() => setAssetDialog(null)}
        onDone={handleAssetDone}
      />
      {isAdmin && historyAsset && (
        <AssetHistory asset={historyAsset} onClose={() => setHistoryAsset(null)} />
      )}
      {isAdmin && logsAsset && (
        <RenderLogsModal asset={logsAsset} onClose={() => setLogsAsset(null)} />
      )}
      <ComparePickerDialog
        open={compareSourceAsset !== null}
        sourceAsset={compareSourceAsset}
        // O B costuma morar ao lado do A: o seletor abre na pasta do arquivo de
        // origem — a pasta aberta, ou a dele quando veio de uma busca recursiva.
        initialFolder={compareSourceAsset?.folder_path ?? folder}
        onClose={() => setCompareSourceAsset(null)}
        onPick={handleComparePick}
      />
      <UploadConfirmDialog
        pending={pendingUpload}
        onCancel={() => setPendingUpload(null)}
        onConfirm={confirmPendingUpload}
      />
      <UploadQueuePanel
        items={uploadItems}
        skipped={uploadSkipped}
        systemCount={uploadSystemCount}
        onClearFinished={clearFinishedUploads}
        onCancelQueued={cancelQueuedUploads}
      />
    </div>
  );
}
